import React, { useCallback, useRef, useState } from 'react';

import { Button, Frame, Layout, Page, Toast, Banner } from '@shopify/polaris';
import axios from 'axios';
import Papa from 'papaparse';

import { TopBarMarkup, NavigationMarkup } from '../../components';
import { useUser } from '../../utils/PrivateRoute';
import { PolicyList } from './components/';
import { Organization, Order } from '../../types';

export function PolicyAll() {
  const { user } = useUser();

  const skipToContentRef = useRef<HTMLAnchorElement>(null);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const toggleMobileNavigationActive = useCallback(() => setMobileNavigationActive((mobileNavigationActive) => !mobileNavigationActive), []);

  const handleMobileNavigation = () => {
    setMobileNavigationActive((data) => !data);
  };

  const [active, setActive] = useState<boolean | { message: string }>(false);
  const [saveError, setSaveError] = useState<boolean | { message: string }>(false);
  const [counterError, setCounterError] = useState(false);
  const [viewValue, setViewValue] = useState<'all' | 'paid' | 'emitted' | 'expired'>('all');

  const toggleActive = useCallback(() => setActive(false), []);

  // Export policies to CSV based on the current view
  const handleExportCSV = useCallback(async () => {
    try {
      // Fetch policies based on the current viewValue
      const response = await axios.get(`${process.env.REACT_APP_API_URL ?? '/api'}/admin/policies?view=${viewValue}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('mb__access_token')}`,
        },
      });

      const data = response.data;

      if (data.status !== 'success' || !data.data || data.data.length === 0) {
        setSaveError({ message: `Nessuna polizza trovata per la vista "${viewValue}"` });
        return;
      }

      const orders: Order[] = data.data;

      // Prepare CSV data
      const csvData = orders.map((order) => {
        const productName = order.line_items.length > 0 ? `${order.line_items[0].product?.name || 'N/A'} - ${order.line_items[0].name || 'N/A'}` : 'N/A';

        const organizationName =
          order.organization && typeof order.organization === 'object' && 'name' in order.organization ? (order.organization as Organization).name : 'N/A';

        // Handle date formatting
        const dateCreated = order.date_created
          ? new Date(order.date_created).toLocaleDateString('it-IT', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
          : 'N/A';

        const effectiveDate = order.date_start
          ? new Date(order.date_start).toLocaleDateString('it-IT', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
          : 'In attesa';

        const dateEnd = order.date_end
          ? new Date(order.date_end).toLocaleDateString('it-IT', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            })
          : 'N/A';

        // Handle total price formatting
        const total_price = order.total_price !== undefined ? order.total_price.toFixed(2).replace('.', ',') + ' €' : '0,00 €';

        return {
          cliente: `${order.user?.lastname || ''} ${order.user?.name || ''}`.trim() || 'N/A',
          email: order.user?.email || 'N/A',
          prodotto: productName,
          organizzazione: organizationName,
          data_creazione: dateCreated,
          data_decorrenza: effectiveDate,
          data_fine: dateEnd,
          stato: order.status,
          totale: total_price,
        };
      });

      // Define CSV fields
      const fields = [
        { label: 'Cliente', value: 'cliente' },
        { label: 'Email', value: 'email' },
        { label: 'Prodotto', value: 'prodotto' },
        { label: 'Organizzazione', value: 'organizzazione' },
        { label: 'Data Creazione', value: 'data_creazione' },
        { label: 'Data Decorrenza', value: 'data_decorrenza' },
        { label: 'Data Fine', value: 'data_fine' },
        { label: 'Stato', value: 'stato' },
        { label: 'Totale', value: 'totale' },
      ];

      // Generate CSV
      const csv = Papa.unparse(csvData, {
        columns: fields.map((field) => field.value),
        delimiter: ';',
        header: true,
        quotes: true,
      });

      // Adding BOM for UTF-8 support
      const BOM = '\uFEFF';
      const csvWithBOM = BOM + csv;

      // Determine the file name based on viewValue
      const fileNamePrefix = {
        all: 'tutte_le_polizze',
        paid: 'polizze_pagate',
        emitted: 'polizze_emesse',
        expired: 'polizze_scadute',
      }[viewValue];

      // Trigger download
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileNamePrefix}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Show success message
      setActive({ message: 'Esportazione CSV completata con successo' });
    } catch (error) {
      console.error("Errore durante l'esportazione del CSV:", error);
      setSaveError({ message: "Errore durante l'esportazione del CSV. Riprova più tardi." });
    }
  }, [viewValue, setSaveError, setActive]);

  /**
   * Error markups & toast
   */
  const toastMarkup = active ? (
    <Toast content={typeof active === 'object' ? active.message : 'I dati sono stati aggiornati con successo.'} onDismiss={toggleActive} />
  ) : null;

  const saveErrorMarkup = saveError && (
    <Layout.Section>
      <Banner title="Si è verificato un errore" status="critical" onDismiss={() => setSaveError(false)}>
        <p>{typeof saveError === 'object' ? saveError.message : 'Si è pregati di riprovare più tardi.'}</p>
      </Banner>
    </Layout.Section>
  );

  const counterErrorMarkup = counterError && (
    <Layout.Section>
      <Banner title={'Uno o più polizze non possono essere rimosse'} status="critical" onDismiss={() => setCounterError(false)}>
        <p>Le polizze sostituite non possono essere rimosse. Si è pregati di controllare e riprovare.</p>
      </Banner>
    </Layout.Section>
  );

  // ---- Page markup ----
  const pageMarkup = (
    <Page
      fullWidth
      title="Polizze"
      primaryAction={
        <Button primary url="/orders/new">
          Crea preventivo
        </Button>
      }
      secondaryActions={[
        {
          content: `Esporta polizze in CSV`,
          onAction: handleExportCSV,
        },
      ]}
    >
      <Layout>
        {/* Banner */}
        {saveErrorMarkup}
        {counterErrorMarkup}
        {/* List */}
        <Layout.Section>
          <PolicyList setActive={setActive} setSaveError={setSaveError} setCounterError={setCounterError} user={user} setViewValue={setViewValue} />
        </Layout.Section>
      </Layout>
    </Page>
  );

  return (
    <Frame
      topBar={<TopBarMarkup user={user} handleMobileNavigation={handleMobileNavigation} />}
      navigation={<NavigationMarkup user={user} />}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
      skipToContentTarget={skipToContentRef}
    >
      {pageMarkup}
      {toastMarkup}
    </Frame>
  );
}
